import { Center, createTheme, Loader, MantineThemeProvider, useMantineColorScheme } from '@mantine/core';
import { DatePickerInput, DateTimePicker } from '@mantine/dates';
import { useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { useSession } from '@/api/session';
import { useSystemInfo } from '@/api/system';
import { useFeatures, useWorkspace } from '@/api/workspace';
import { NoWorkspace } from '@/features/workspace/NoWorkspace';
import dayjs from '@/lib/dayjs';
import { MaintenancePage, TwoFactorRequiredPage } from './Gates';
import { useRealtime } from './useRealtime';
import { buildWorkspaceContext, WorkspaceCtx } from './WorkspaceContext';

/** Lädt Sitzung, aktiven Workspace und Features; leitet ohne Anmeldung zum Login. */
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const session = useSession();
  const system = useSystemInfo();
  const user = session.data?.user;
  const workspaceId = user?.activeWorkspaceId;
  const workspace = useWorkspace(workspaceId);
  const features = useFeatures(workspaceId);
  const { setColorScheme } = useMantineColorScheme();
  useRealtime(!!user);
  const theme = user?.preferences.theme;

  useEffect(() => {
    if (theme) setColorScheme(theme === 'DARK' ? 'dark' : theme === 'LIGHT' ? 'light' : 'auto');
  }, [theme, setColorScheme]);

  useEffect(() => {
    const lang = user?.preferences.locale;
    if (lang && lang !== i18n.language) {
      void i18n.changeLanguage(lang);
    }
    dayjs.locale(lang === 'en' ? 'en' : 'de');
  }, [user?.preferences.locale, i18n]);

  const value = useMemo(() => {
    if (!workspace.data || !features.data || !user) return null;
    return buildWorkspaceContext(
      workspace.data,
      features.data.features.filter((f) => f.enabled).map((f) => f.feature),
      user,
    );
  }, [workspace.data, features.data, user]);

  if (session.isPending) {
    return <FullPageLoader />;
  }
  if (!session.data?.authenticated || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (system.data?.maintenanceMessage && !user.superadmin) {
    return <MaintenancePage message={system.data.maintenanceMessage} />;
  }
  if (!workspaceId || workspace.isError) {
    return <NoWorkspace />;
  }
  if (!value) {
    return <FullPageLoader />;
  }
  const needs2fa =
    value.workspace.settings.security.require2fa && !user.twoFactorEnabled && session.data.ssoWorkspaceId !== value.workspaceId;
  return (
    <WorkspaceCtx.Provider value={value}>
      <MantineThemeProvider theme={dateTheme(user.preferences.dateFormat, user.preferences.timeFormat)}>
        {needs2fa ? <TwoFactorRequiredPage /> : children}
      </MantineThemeProvider>
    </WorkspaceCtx.Provider>
  );
}

/** Datumsfelder zeigen das Datumsformat aus dem Profil (PROF-02). */
function dateTheme(dateFormat: string, timeFormat: string) {
  const time = timeFormat === 'H12' ? 'h:mm A' : 'HH:mm';
  return createTheme({
    components: {
      DatePickerInput: DatePickerInput.extend({ defaultProps: { valueFormat: dateFormat } }),
      DateTimePicker: DateTimePicker.extend({ defaultProps: { valueFormat: `${dateFormat} ${time}` } }),
    },
  });
}

export function FullPageLoader() {
  return (
    <Center mih="100dvh">
      <Loader />
    </Center>
  );
}
